/**
 * Browser-compatible HCL Parser Service
 *
 * Wraps the @cdktf/hcl2json WASM library for browser usage.
 * Uses pako for gzip decompression since the browser doesn't have Node.js zlib.
 *
 * This eliminates the need for backend WASM parsing, which can crash the
 * entire Backstage process if the WASM runtime encounters an error.
 */

import pako from 'pako';

// Types for the Go WASM bridge
interface GoBridge {
  parse: (filename: string, hcl: string) => Promise<string>;
}

// Global state for WASM singleton
let wasmInstance: GoBridge | null = null;
let wasmLoadPromise: Promise<GoBridge> | null = null;
let wasmLoadError: Error | null = null;

// Root object that the Go WASM code registers functions on
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const jsRoot: Record<string, any> = {};

/**
 * Sleep helper for waiting on WASM initialization
 */
function sleep(ms: number = 0): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Load the Go WASM runtime (wasm_exec.js)
 * This must be done before instantiating the WASM module
 */
async function loadWasmRuntime(): Promise<void> {
  // Check if Go class is already defined (runtime already loaded)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).Go) {
    console.log('[hclParser] Go WASM runtime already loaded');
    return;
  }

  console.log(
    '[hclParser] Loading Go WASM runtime from /assets/hcl2json/wasm_exec.js',
  );
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/assets/hcl2json/wasm_exec.js';
    script.onload = () => {
      console.log('[hclParser] Go WASM runtime loaded successfully');
      resolve();
    };
    script.onerror = err => {
      console.error('[hclParser] Failed to load Go WASM runtime:', err);
      reject(new Error('Failed to load Go WASM runtime'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Fetch and decompress the WASM binary
 */
async function fetchWasmBinary(): Promise<ArrayBuffer> {
  console.log(
    '[hclParser] Fetching WASM binary from /assets/hcl2json/main.wasm.gz',
  );
  const response = await fetch('/assets/hcl2json/main.wasm.gz');
  console.log('[hclParser] WASM fetch response status:', response.status);
  if (!response.ok) {
    throw new Error(`Failed to fetch WASM binary: ${response.statusText}`);
  }

  const compressedData = await response.arrayBuffer();
  console.log('[hclParser] Compressed WASM size:', compressedData.byteLength);
  const decompressed = pako.ungzip(new Uint8Array(compressedData));
  console.log('[hclParser] Decompressed WASM size:', decompressed.byteLength);
  return decompressed.buffer;
}

/**
 * Create a Go bridge proxy that waits for WASM to be ready
 */
function createGoBridge(wasmBytes: ArrayBuffer): Promise<GoBridge> {
  console.log('[hclParser] Creating Go bridge...');
  return new Promise(async (resolve, reject) => {
    let ready = false;
    const timeout = setTimeout(() => {
      if (!ready) {
        console.error(
          '[hclParser] WASM initialization timed out after 30 seconds',
        );
        reject(new Error('WASM initialization timed out after 30 seconds'));
      }
    }, 30000);

    try {
      // Load the Go WASM runtime
      console.log('[hclParser] Loading Go WASM runtime...');
      await loadWasmRuntime();

      // Create Go instance
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      console.log('[hclParser] Creating Go instance...');
      const go = new (window as any).Go();

      // Instantiate the WASM module
      console.log('[hclParser] Instantiating WASM module...');
      const result = await WebAssembly.instantiate(wasmBytes, go.importObject);
      console.log('[hclParser] WASM module instantiated successfully');

      // CRITICAL: The Go WASM code uses js.Global() which returns globalThis.
      // We must register __parse_terraform_config_wasm__ on globalThis BEFORE go.run().
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const globalObj = globalThis as any;

      // Register the jsRoot on all possible global locations for compatibility
      globalObj.__parse_terraform_config_wasm__ = jsRoot;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__parse_terraform_config_wasm__ = jsRoot;

      // Polyfill global for Node.js-style code
      if (typeof globalObj.global === 'undefined') {
        globalObj.global = globalObj;
      }
      globalObj.global.__parse_terraform_config_wasm__ = jsRoot;

      console.log(
        '[hclParser] Registered __parse_terraform_config_wasm__ on globalThis, window, and global',
      );

      // Run the Go WASM module (this populates jsRoot)
      console.log('[hclParser] Running Go WASM module...');
      void go.run(result.instance);

      // Wait a tick for Go to register its functions
      console.log('[hclParser] Waiting for Go to register functions...');
      await sleep(100);

      // Debug: Check what got registered
      console.log('[hclParser] jsRoot contents:', Object.keys(jsRoot));
      console.log(
        '[hclParser] window.__parse_terraform_config_wasm__:',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Object.keys((window as any).__parse_terraform_config_wasm__ || {}),
      );

      // Check common Go WASM registration patterns
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      console.log('[hclParser] Checking window for WASM functions:', {
        parse: typeof w.parse,
        hcl2json: typeof w.hcl2json,
        parseHCL: typeof w.parseHCL,
        parseHcl: typeof w.parseHcl,
        // Check if there's a global hcl object
        hcl: typeof w.hcl,
      });

      // Try to find any new functions on window that might be from Go
      const windowKeys = Object.keys(window).filter(
        k =>
          typeof w[k] === 'function' &&
          ![
            'addEventListener',
            'removeEventListener',
            'dispatchEvent',
          ].includes(k),
      );
      console.log('[hclParser] Functions on window:', windowKeys.slice(0, 20));

      ready = true;
      clearTimeout(timeout);

      // Create proxy that waits for functions and handles callbacks
      const proxy = new Proxy({} as GoBridge, {
        get: (_, key: string) => {
          // Ignore special JavaScript properties used for Promise/thenable detection
          // When await is used, JS checks for .then to see if something is a Promise
          if (key === 'then' || key === 'catch' || key === 'finally') {
            return undefined;
          }

          return async (...args: unknown[]) => {
            // Wait for the function to be registered
            let attempts = 0;
            while (!(key in jsRoot) && attempts < 100) {
              await sleep(50);
              attempts++;
            }

            if (!(key in jsRoot)) {
              throw new Error(`WASM function "${key}" not available`);
            }

            if (typeof jsRoot[key] !== 'function') {
              return jsRoot[key];
            }

            return new Promise((resolveCall, rejectCall) => {
              const cb = (err: string | null, ...msg: unknown[]) => {
                if (err) {
                  rejectCall(new Error(err));
                } else {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  resolveCall(msg[0] as any);
                }
              };
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (jsRoot[key] as any).apply(undefined, [...args, cb]);
            });
          };
        },
      });

      resolve(proxy);
    } catch (error) {
      clearTimeout(timeout);
      reject(error);
    }
  });
}

/**
 * Initialize the WASM module (singleton pattern)
 */
async function initWasm(): Promise<GoBridge> {
  console.log('[hclParser] initWasm called', {
    hasInstance: !!wasmInstance,
    hasError: !!wasmLoadError,
    hasPromise: !!wasmLoadPromise,
  });

  // Return existing instance if available
  if (wasmInstance) {
    console.log('[hclParser] Returning existing WASM instance');
    return wasmInstance;
  }

  // Return existing error if initialization already failed
  if (wasmLoadError) {
    console.error('[hclParser] Returning cached WASM error:', wasmLoadError);
    throw wasmLoadError;
  }

  // Return existing promise if initialization is in progress
  if (wasmLoadPromise) {
    console.log(
      '[hclParser] WASM initialization in progress, returning existing promise',
    );
    return wasmLoadPromise;
  }

  // Start initialization
  console.log('[hclParser] Starting WASM initialization...');
  wasmLoadPromise = (async () => {
    try {
      const wasmBytes = await fetchWasmBinary();
      wasmInstance = await createGoBridge(wasmBytes);
      console.log('[hclParser] WASM initialization complete');
      return wasmInstance;
    } catch (error) {
      console.error('[hclParser] WASM initialization failed:', error);
      wasmLoadError = error instanceof Error ? error : new Error(String(error));
      wasmInstance = null;
      wasmLoadPromise = null;
      throw wasmLoadError;
    }
  })();

  return wasmLoadPromise;
}

/**
 * Parsed HCL output structure
 */
export interface ParsedHcl {
  module?: Record<string, Array<Record<string, unknown>>>;
  variable?: Record<string, Array<Record<string, unknown>>>;
  resource?: Record<string, Record<string, unknown[]>>;
  data?: Record<string, Record<string, unknown[]>>;
  output?: Record<string, unknown[]>;
  locals?: Record<string, unknown>[];
  [key: string]: unknown;
}

/**
 * Module block extracted from HCL
 */
export interface ModuleBlock {
  name: string;
  source: string;
  version?: string;
  currentValues: Record<string, unknown>;
}

/**
 * Parse HCL content in the browser using WASM
 *
 * @param fileName - Name of the file being parsed (for error messages)
 * @param content - HCL content to parse
 * @returns Parsed HCL as a JavaScript object
 * @throws Error if parsing fails
 */
export async function parseHcl(
  fileName: string,
  content: string,
): Promise<ParsedHcl> {
  console.log('[hclParser] parseHcl called', {
    fileName,
    contentLength: content.length,
  });
  try {
    const wasm = await initWasm();
    console.log('[hclParser] WASM initialized, calling parse...');
    const resultJson = await wasm.parse(fileName, content);
    console.log(
      '[hclParser] Parse returned, result length:',
      resultJson.length,
    );
    const parsed = JSON.parse(resultJson) as ParsedHcl;
    console.log('[hclParser] Parsed result:', {
      hasModule: !!parsed.module,
      hasVariable: !!parsed.variable,
      keys: Object.keys(parsed),
    });
    return parsed;
  } catch (error) {
    console.error('[hclParser] parseHcl error:', error);
    throw error;
  }
}

/**
 * Extract module blocks from parsed HCL
 *
 * @param parsed - Parsed HCL output
 * @returns Array of module blocks with their configurations
 */
export function extractModuleBlocks(parsed: ParsedHcl): ModuleBlock[] {
  const moduleBlocks: ModuleBlock[] = [];

  if (!parsed.module) {
    return moduleBlocks;
  }

  for (const [moduleName, moduleConfigs] of Object.entries(parsed.module)) {
    const moduleConfig = Array.isArray(moduleConfigs)
      ? moduleConfigs[0]
      : moduleConfigs;

    if (moduleConfig && typeof moduleConfig === 'object') {
      const { source, version, ...currentValues } = moduleConfig;

      if (source && typeof source === 'string') {
        moduleBlocks.push({
          name: moduleName,
          source: source as string,
          version: version as string | undefined,
          currentValues: currentValues as Record<string, unknown>,
        });
      }
    }
  }

  return moduleBlocks;
}

/**
 * Check if WASM is available (for feature detection)
 */
export function isWasmAvailable(): boolean {
  return typeof WebAssembly !== 'undefined';
}

/**
 * Reset the WASM instance (useful for testing or recovery)
 */
export function resetWasmInstance(): void {
  wasmInstance = null;
  wasmLoadPromise = null;
  wasmLoadError = null;
}
